import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { fmtDate, fmtDateTime } from '@/lib/datetime';
import { ContractStatusBadge, GateDecisionBadge } from './ContractStatusBadge';
import type { ContractSummaryRow, SortField, SortDirection } from './types';

interface ContractsTableProps {
  contracts: ContractSummaryRow[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
}

interface ColumnDef {
  field: SortField | null;
  label: string;
  className?: string;
}

const COLUMNS: ColumnDef[] = [
  { field: 'contract_number', label: 'Contract #', className: 'w-36' },
  { field: 'title', label: 'Title', className: 'min-w-[200px]' },
  { field: 'status', label: 'Status', className: 'w-32' },
  { field: null, label: 'Gate Decision', className: 'w-40' },
  { field: 'contract_value', label: 'Value', className: 'w-36 text-right' },
  { field: 'end_date', label: 'Expires', className: 'w-28' },
  // Upload time + version disambiguate duplicate file names. Per-second
  // precision; sortable.
  { field: 'updated_at', label: 'Uploaded', className: 'w-44' },
];

function SortIcon({ field, sortField, sortDirection }: { field: SortField | null; sortField: SortField; sortDirection: SortDirection }) {
  if (!field || field !== sortField) return null;
  return sortDirection === 'asc'
    ? <ChevronUp className="h-3 w-3 ml-1 inline" aria-hidden="true" />
    : <ChevronDown className="h-3 w-3 ml-1 inline" aria-hidden="true" />;
}

function formatDate(dateStr: string | null | undefined): string {
  return fmtDate(dateStr) || '—';
}

function formatValue(value: string | null | undefined, currency: string): string {
  if (!value) return '—';
  return `${currency} ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ContractsTable({
  contracts, isLoading, total, page, pageSize,
  sortField, sortDirection, onSort, onPageChange,
}: ContractsTableProps) {
  const navigate = useNavigate();
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  if (isLoading) {
    return (
      <div role="status" className="space-y-2" aria-busy="true" aria-label="Loading contracts">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (contracts.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" role="grid" aria-label="Contracts list">
          <thead className="bg-muted/50">
            <tr>
              {COLUMNS.map((col) => {
                const colField = col.field;
                return (
                  <th
                    key={col.label}
                    scope="col"
                    className={cn(
                      'px-3 py-2 text-left font-medium text-muted-foreground',
                      col.className,
                      colField && 'cursor-pointer select-none hover:text-foreground',
                    )}
                    onClick={colField ? () => { onSort(colField); } : undefined}
                    aria-sort={colField === sortField ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.label}
                    <SortIcon field={colField} sortField={sortField} sortDirection={sortDirection} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contracts.map((contract) => (
              <tr
                key={contract.id}
                className="hover:bg-muted/30 cursor-pointer transition-colors focus-within:bg-muted/30"
                onClick={() => { navigate(`/contracts/${contract.id}`); }}
                role="row"
              >
                <td className="px-3 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {contract.contract_number}
                </td>
                <td className="px-3 py-3 max-w-xs">
                  <span className="block truncate font-medium" title={contract.title}>
                    {contract.title}
                    {contract.version > 1 && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        v{contract.version}
                      </span>
                    )}
                  </span>
                  {contract.vendor_name && (
                    <span className="text-xs text-muted-foreground">{contract.vendor_name}</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <ContractStatusBadge status={contract.status} />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <GateDecisionBadge decision={contract.gate_decision} />
                    {contract.job_id !== null && (
                      <button
                        className="text-xs text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded"
                        aria-label={`View job for contract ${contract.contract_number}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${contract.job_id ?? ''}`); }}
                      >
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {formatValue(contract.contract_value, contract.currency)}
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                  {formatDate(contract.end_date)}
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap text-xs font-mono">
                  {fmtDateTime(contract.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span aria-live="polite">
          Showing {start}–{end} of {total}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onPageChange(page - 1); }}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <RefreshCw className="h-3 w-3 rotate-180 mr-1" aria-hidden="true" />
            Previous
          </Button>
          <span aria-label={`Page ${page} of ${totalPages}`}>
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onPageChange(page + 1); }}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            Next
            <RefreshCw className="h-3 w-3 ml-1" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
